<template>
  <div v-if="show" class="standalone-modal-overlay" @click.self="closeModal">
    <div class="standalone-modal-content" :style="{ maxWidth: '600px' }">
      <!-- Modal Header -->
      <div class="standalone-modal-header">
        <h5 class="modal-title-custom">Fix Error {{ errorIndex + 1 }} of {{ totalErrors }}</h5>
        <button type="button" class="btn-close-custom" @click="closeModal" aria-label="Close" :disabled="isLoading">&times;</button>
      </div>

      <!-- Modal Body -->
      <div class="standalone-modal-body">
        <form @submit.prevent="handleValidate">
          <div class="form-group-custom mb-3">
            <label for="bank" class="form-label-custom">Bank*</label>
            <select id="bank" class="form-control-custom" v-model="editableItem.bank" :disabled="isLoading">
              <option disabled value="">Please select a bank</option>
              <option 
                v-for="bank_option in banks" 
                :key="bank_option.bank_name" 
                :value="bank_option.bank_name"
                :selected="bank_option.bank_name === editableItem.bank"
              >
                {{ bank_option.bank_name }}
              </option>
            </select>
          </div>

          <div class="form-group-custom mb-3">
            <label for="accountNumber" class="form-label-custom">Account Number*</label>
            <div class="input-group-custom">
              <input 
                type="text" 
                id="accountNumber" 
                class="form-control-custom"
                v-model="editableItem['account_number']"
                placeholder="Enter account number"
                :disabled="isLoading"
              />
              <button type="button" class="btn-check-custom" @click="checkAccountNumber" :disabled="isLoading">Check</button>
            </div>
          </div>

          <div class="form-group-custom mb-3">
            <label for="accountName" class="form-label-custom">Account Name*</label>
            <input 
              type="text" 
              id="accountName" 
              class="form-control-custom form-control-filled-custom"
              v-model="editableItem['account_name']"
              placeholder=""
              :disabled="isLoading"
            />
          </div>

          <div class="form-group-custom mb-4">
            <label for="amount" class="form-label-custom">Amount (IDR)*</label>
            <input 
              type="number" 
              id="amount" 
              class="form-control-custom"
              v-model="editableItem['amount']"
              placeholder="50,000"
              :disabled="isLoading"
            />
          </div>
          
          <div v-if="internalErrorMessage" class="alert-custom alert-danger-custom mb-3" role="alert">
            {{ internalErrorMessage }}
          </div>
           <div v-if="isLoading" class="text-center my-3">
            <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            Validating...
          </div>
        </form>
      </div>

      <!-- Modal Footer -->
      <div class="standalone-modal-footer">
        <button type="button" class="btn-skip-custom" @click="skipError" :disabled="isLoading">Skip</button>
        <button type="button" class="btn-validate-custom" @click="handleValidate" :disabled="isLoading">
          <span v-if="isLoading" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          {{ isLoading ? 'Validating...' : 'Validate' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, defineProps, defineEmits, onMounted } from 'vue';
import { fixErrorsBatchfileByRow, getBanks, checkAccountNumber as apiCheckAccountNumber } from '@/api/transfer'; // Import the API function

const props = defineProps({
  show: Boolean,
  errorItem: Object,
  errorIndex: Number,
  totalErrors: Number,
  batchUUID: String, // Add batchUUID as a prop to pass to the API
});

const emit = defineEmits(['close', 'save-error', 'skip-error', 'validation-start', 'validation-complete']);

const editableItem = ref({});
const internalErrorMessage = ref('');
const isLoading = ref(false); // For loading state during API call
const banks = ref([]); // To store the list of banks

// Fetch banks when the component is mounted
onMounted(async () => {
  try {
    const response = await getBanks();
    if (response && response.status === 'success' && Array.isArray(response.data)) {
      banks.value = response.data;
    } else {
      console.error('Failed to load banks:', response.message || 'Unknown error');
      internalErrorMessage.value = 'Failed to load bank list.';
    }
  } catch (error) {
    console.error('Error fetching banks:', error);
    internalErrorMessage.value = 'Error fetching bank list.';
  }
});

watch(() => props.errorItem, (newItem) => {
  if (newItem) {
    editableItem.value = { ...newItem };
    // Prioritize API error message if available, otherwise use local validation message
    internalErrorMessage.value = newItem.error_message || ''; 
  } else {
    editableItem.value = {};
    internalErrorMessage.value = '';
  }
}, { immediate: true, deep: true });

const closeModal = () => {
  if (isLoading.value) return; // Prevent closing while loading
  emit('close');
};

const checkAccountNumber = async () => {
  if (!editableItem.value.bank || !editableItem.value['account_number']) {
    internalErrorMessage.value = 'Bank and Account Number are required to check.';
    return;
  }
  isLoading.value = true;
  internalErrorMessage.value = '';
  try {
    const formData = new FormData();
    formData.append('bank', editableItem.value.bank);
    formData.append('account_number', editableItem.value['account_number']);

    const response = await apiCheckAccountNumber(formData);

    if (response && response.status === 'success' && response.data) {
      editableItem.value['account_name'] = response.data.account_name;
      internalErrorMessage.value = ''; // Clear error if successful
    } else {
      editableItem.value['account_name'] = '';
      internalErrorMessage.value = response.message || (response.data && response.data.message) || 'Failed to verify account details.';
    }
  } catch (error) {
    console.error('Error checking account number:', error);
    editableItem.value['account_name'] = '';
    internalErrorMessage.value = error.message || 'An unexpected error occurred while checking account.';
  } finally {
    isLoading.value = false;
  }
};

const validateLocally = () => { // Renamed from validateData to avoid confusion
  if (!editableItem.value.bank) {
    internalErrorMessage.value = 'Bank is required.';
    return false;
  }
  if (!editableItem.value['account_number']) {
    internalErrorMessage.value = 'Account Number is required.';
    return false;
  }
  if (!editableItem.value['account_name']) {
    // This might be auto-filled by 'Check' or pre-filled by API, so this validation might change
    internalErrorMessage.value = 'Account Name is required. Please use the Check button if not auto-filled.';
    return false;
  }
  if (editableItem.value.amount === undefined || editableItem.value.amount === null || parseFloat(editableItem.value.amount) <= 0) {
    internalErrorMessage.value = 'Amount must be a positive number.';
    return false;
  }
  internalErrorMessage.value = '';
  return true;
};

const handleValidate = async () => {
  if (!validateLocally()) {
    return;
  }
  isLoading.value = true;
  emit('validation-start');
  internalErrorMessage.value = ''; // Clear previous messages

  try {
    const formData = new FormData();
    // formData.append('uuid', props.errorItem.uuid); 
    formData.append('uuid', editableItem.value.uuid); 
    formData.append('bank', editableItem.value.bank);
    formData.append('account_number', editableItem.value.account_number);
    formData.append('account_name', editableItem.value.account_name);
    formData.append('amount', editableItem.value.amount);
    // Add admin_fee if it's editable and part of the API request
    if (editableItem.value.admin_fee !== undefined) {
        formData.append('admin_fee', editableItem.value.admin_fee);
    }

    const response = await fixErrorsBatchfileByRow(formData);

    if (response && response.status === 'success') { // Or check response.data.status
      // API call was successful, the row is considered fixed by the backend.
      // Emit the originally intended data, but now it's confirmed by API.
      emit('save-error', { 
        ...editableItem.value, 
        id: editableItem.value.id, // ensure the original ID is part of the emitted data
        status: 'Success', // Backend should confirm this status
        error_message: null 
      });
    } else {
      // API returned an error or unsuccessful status
      internalErrorMessage.value = response.message || (response.data && response.data.message) || 'Validation failed on server.';
      // Optionally, parse more detailed errors if response.data.errors exists
      if (response && response.data && response.data.errors && typeof response.data.errors === 'object') {
        const errorMessages = Object.values(response.data.errors).flat().join(', ');
        internalErrorMessage.value = errorMessages || internalErrorMessage.value;
      }
    }
  } catch (error) {
    console.error('Error validating row with API:', error);
    internalErrorMessage.value = error.message || 'An unexpected error occurred during validation.';
  } finally {
    isLoading.value = false;
    emit('validation-complete');
  }
};

const skipError = () => {
  if (isLoading.value) return;
  emit('skip-error', editableItem.value);
};

</script>

<style scoped>
.standalone-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000; /* Ensure it's on top */
}

.standalone-modal-content {
  background-color: #fff;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  width: 90%;
  max-width: 500px; /* Default max-width, can be overridden by prop */
  display: flex;
  flex-direction: column;
}

.standalone-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 15px;
  /* border-bottom: 1px solid #eee; */ /* Optional: if you want a line */
}

.modal-title-custom {
  font-size: 1.5rem; /* Matches image: "Fix Error 1 of 3" */
  font-weight: bold;
  color: #333;
  margin: 0;
}

.btn-close-custom {
  background: none;
  border: none;
  font-size: 1.75rem;
  font-weight: lighter;
  color: #888;
  cursor: pointer;
  padding: 0;
  line-height: 1;
}

.standalone-modal-body {
  padding-top: 15px;
  padding-bottom: 20px;
}

.form-group-custom {
  margin-bottom: 1rem; /* Consistent spacing */
}

.form-label-custom {
  display: block;
  margin-bottom: .5rem;
  font-size: 0.875rem;
  color: #555;
  font-weight: 500;
}

.form-control-custom, .form-control-custom:disabled {
  width: 100%;
  padding: 0.75rem 1rem;
  font-size: 1rem;
  border: 1px solid #ced4da;
  border-radius: 0.375rem; /* Bootstrap-like radius */
  box-sizing: border-box;
  transition: border-color .15s ease-in-out,box-shadow .15s ease-in-out;
}

.form-control-custom:focus {
  border-color: #86b7fe;
  outline: 0;
  box-shadow: 0 0 0 0.25rem rgba(13,110,253,.25);
}

.form-control-filled-custom {
  background-color: #e9f5e9; /* Light green fill */
  border-color: #a8d8a8;
}

.input-group-custom {
  display: flex;
}

.input-group-custom .form-control-custom {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  flex-grow: 1;
}

.btn-check-custom {
  padding: 0.75rem 1.25rem;
  background-color: #28a745; /* Green */
  color: white;
  border: 1px solid #28a745;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  border-top-right-radius: 0.375rem;
  border-bottom-right-radius: 0.375rem;
  cursor: pointer;
  font-size: 1rem;
  transition: background-color .15s ease-in-out;
}

.btn-check-custom:hover {
  background-color: #218838;
}

.standalone-modal-footer {
  display: flex;
  justify-content: flex-end; /* Align buttons to the right */
  padding-top: 15px;
  /* border-top: 1px solid #eee; */ /* Optional: if you want a line */
  gap: 10px; /* Space between buttons */
}

.btn-skip-custom, .btn-validate-custom {
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  border-radius: 0.375rem;
  cursor: pointer;
  border: 1px solid transparent;
  font-weight: 500;
}

.btn-skip-custom {
  background-color: #f8f9fa; /* Light grey, similar to image */
  color: #333;
  border-color: #ddd;
}

.btn-skip-custom:hover {
  background-color: #e2e6ea;
}

.btn-validate-custom {
  background-color: #007bff; /* Blue */
  color: white;
}

.btn-validate-custom:hover {
  background-color: #0056b3;
}

.alert-custom {
  padding: .75rem 1.25rem;
  margin-bottom: 1rem;
  border: 1px solid transparent;
  border-radius: .25rem;
  font-size: 0.9rem;
}

.alert-danger-custom {
  color: #721c24;
  background-color: #f8d7da;
  border-color: #f5c6cb;
}
</style>